import { Button, Container, Element, Label, SelectInput, TextInput } from '@playcanvas/pcui';

import { Events } from '../events';
import { localize } from './localization';
import uploadIcon from './svg/import.svg'; // Reuse import icon
import { AuthService } from '../auth';

const createSvg = (svgString: string, args = {}) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new Element({
        dom: new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement,
        ...args
    });
};

class UploadDialog extends Container {
    show: () => Promise<boolean>;
    hide: () => void;
    destroy: () => void;

    constructor(events: Events, authService: AuthService, args = {}) {
        args = {
            ...args,
            id: 'upload-dialog',
            class: 'settings-dialog',
            hidden: true,
            tabIndex: -1
        };

        super(args);

        const dialog = new Container({
            id: 'dialog'
        });

        // header
        const headerIcon = createSvg(uploadIcon, { id: 'icon' });
        const headerText = new Label({ id: 'text', text: localize('user.upload.header') });
        const header = new Container({ id: 'header' });
        header.append(headerIcon);
        header.append(headerText);

        // mode
        const modeLabel = new Label({ class: 'label', text: localize('user.upload.photos') });
        const modeSelect = new SelectInput({
            class: 'select',
            defaultValue: 'photos',
            options: [
                { v: 'photos', t: localize('user.upload.photos') },
                { v: 'video', t: localize('user.upload.video') }
            ]
        });
        const modeRow = new Container({ class: 'row' });
        modeRow.append(modeLabel);
        modeRow.append(modeSelect);

        // project name
        const nameLabel = new Label({ class: 'label', text: localize('user.upload.project-name') });
        const nameInput = new TextInput({ class: 'text-input' });
        const nameRow = new Container({ class: 'row' });
        nameRow.append(nameLabel);
        nameRow.append(nameInput);

        // description
        const descLabel = new Label({ id: 'upload-desc', class: 'label', text: localize('user.upload.photos-desc') });

        // drop zone
        const dropZone = new Container({ id: 'upload-drop-zone', class: 'drop-zone' });
        const dropLabel = new Label({ text: localize('user.upload.drop-files') });
        const pickButton = new Button({ id: 'upload-pick-button', class: 'button', text: localize('user.upload.pick-files') });
        dropZone.append(dropLabel);
        dropZone.append(pickButton);

        // hidden file input
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.multiple = true;
        fileInput.style.display = 'none';
        this.dom.appendChild(fileInput);

        // selected files list
        const filesList = new Container({ id: 'upload-files-list' });

        // progress bar
        const progressContainer = new Container({ id: 'upload-progress-container', hidden: true });
        const progressFill = new Container({ id: 'upload-progress-fill' });
        const progressLabel = new Label({ id: 'upload-progress-label', text: '0%' });
        progressContainer.append(progressFill);
        progressContainer.append(progressLabel);

        // error message
        const errorLabel = new Label({ id: 'upload-error-label', class: 'label', text: '', hidden: true });

        // content
        const content = new Container({ id: 'content' });
        content.append(nameRow);
        content.append(modeRow);
        content.append(descLabel);
        content.append(dropZone);
        content.append(filesList);
        content.append(progressContainer);
        content.append(errorLabel);

        // footer
        const footer = new Container({ id: 'footer' });
        const cancelButton = new Button({ class: 'button', text: localize('render.cancel') });
        const uploadButton = new Button({ class: 'button', text: localize('user.scenes.upload') });
        footer.append(cancelButton);
        footer.append(uploadButton);

        dialog.append(header);
        dialog.append(content);
        dialog.append(footer);

        this.append(dialog);

        // Listen for upload progress
        events.on('upload.progress', (percent: number) => {
            progressFill.dom.style.width = `${percent}%`;
            progressLabel.text = `Uploading... ${Math.round(percent)}%`;
        });

        let selectedFiles: File[] = [];

        const updateFilesList = () => {
            filesList.clear();
            selectedFiles.forEach((file, index) => {
                const row = new Container({ class: 'upload-file-row' });
                const name = new Label({ text: file.name, class: 'upload-file-name' });
                const remove = new Button({ text: '×', class: 'upload-file-remove' });
                remove.on('click', () => {
                    selectedFiles.splice(index, 1);
                    updateFilesList();
                });
                row.append(name);
                row.append(remove);
                filesList.append(row);
            });
            errorLabel.hidden = true;
        };

        const validateFiles = (files: File[]) => {
            const mode = modeSelect.value;
            const isPhotos = mode === 'photos';
            
            errorLabel.hidden = true;

            for (const file of files) {
                if (isPhotos && !file.type.startsWith('image/')) {
                    errorLabel.text = localize('user.upload.error-mixed');
                    errorLabel.hidden = false;
                    return false;
                }
                if (!isPhotos && !file.type.startsWith('video/')) {
                    errorLabel.text = localize('user.upload.error-mixed');
                    errorLabel.hidden = false;
                    return false;
                }
            }

            if (!isPhotos && files.length > 1) {
                errorLabel.text = localize('user.upload.error-multiple-videos');
                errorLabel.hidden = false;
                return false;
            }

            return true;
        };

        const handleFiles = (files: FileList | File[]) => {
            const newFiles = Array.from(files);
            if (validateFiles(newFiles)) {
                if (modeSelect.value === 'video') {
                    selectedFiles = [newFiles[0]];
                } else {
                    selectedFiles = [...selectedFiles, ...newFiles];
                }
                updateFilesList();
            }
        };

        // Events
        modeSelect.on('change', () => {
            descLabel.text = modeSelect.value === 'photos' ? localize('user.upload.photos-desc') : localize('user.upload.video-desc');
            selectedFiles = [];
            updateFilesList();
            fileInput.accept = modeSelect.value === 'photos' ? 'image/*' : 'video/*';
            fileInput.multiple = modeSelect.value === 'photos';
        });

        dropZone.dom.addEventListener('click', (e) => {
            if (e.target !== pickButton.dom) {
                fileInput.click();
            }
        });
        pickButton.on('click', (e: MouseEvent) => {
            e.stopPropagation();
            fileInput.click();
        });
        fileInput.addEventListener('change', () => handleFiles(fileInput.files));

        const stop = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
        };

        // Accept drops anywhere in the dialog, not just on the drop zone box. The
        // dialog is a full-viewport overlay while open, so this makes the whole page
        // a drop target. drop-handler.ts ignores anything inside .settings-dialog, so
        // the global scene-import handler will not also fire on these drops.
        //
        // dragenter/dragleave fire again for every child element the pointer crosses,
        // so nesting is counted rather than toggling the highlight on each event.
        let dragDepth = 0;

        this.dom.addEventListener('dragenter', (e) => {
            stop(e);
            dragDepth++;
            dropZone.class.add('drag-over');
        });

        this.dom.addEventListener('dragover', stop);

        this.dom.addEventListener('dragleave', (e) => {
            stop(e);
            dragDepth = Math.max(0, dragDepth - 1);
            if (dragDepth === 0) {
                dropZone.class.remove('drag-over');
            }
        });

        this.dom.addEventListener('drop', (e) => {
            stop(e);
            dragDepth = 0;
            dropZone.class.remove('drag-over');
            handleFiles(e.dataTransfer.files);
        });

        let onCancel: () => void;
        let onOK: () => void;

        cancelButton.on('click', () => onCancel());
        uploadButton.on('click', () => onOK());

        this.show = () => {
            selectedFiles = [];
            nameInput.value = '';
            updateFilesList();
            this.hidden = false;
            return new Promise<boolean>((resolve) => {
                onCancel = () => resolve(false);
                onOK = async () => {
                    if (!nameInput.value || nameInput.value.trim() === '') {
                        errorLabel.text = localize('user.upload.error-no-name');
                        errorLabel.hidden = false;
                        return;
                    }
                    if (selectedFiles.length === 0) {
                        errorLabel.text = localize('user.upload.error-no-files');
                        errorLabel.hidden = false;
                        return;
                    }
                    uploadButton.enabled = false;
                    errorLabel.hidden = true;
                    progressContainer.hidden = false;
                    progressFill.dom.style.width = '0%';
                    progressLabel.text = 'Uploading... 0%';

                    const success = await authService.uploadScene(nameInput.value.trim(), selectedFiles);
                    
                    if (success) {
                        resolve(true);
                    } else {
                        uploadButton.enabled = true;
                        progressContainer.hidden = true;
                        errorLabel.text = 'Upload failed. Please try again.';
                        errorLabel.hidden = false;
                    }
                };
            }).finally(() => this.hide());
        };

        this.hide = () => {
            this.hidden = true;
        };

        this.destroy = () => {
            this.hide();
            super.destroy();
        };
    }
}

export { UploadDialog };
